import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';

/**
 * Acciones de una fila de listado.
 *
 * Todas las pantallas pintaban a mano sus botones de editar, activar, archivar, eliminar o imprimir,
 * cada una con su icono, su tamaño y su forma de confirmar. Esto las unifica: el mismo orden, el mismo
 * tamaño y el mismo comportamiento en cualquier listado, que es lo que se pide al usar el ERP.
 *
 * Cada acción se declara con su etiqueta (la del `title`, que es la que se lee al pasar el ratón y la
 * que anuncia un lector de pantalla), su icono y qué hace.
 *
 * Dos reglas para no llenar la fila de iconos muertos:
 * - si la acción **no aplica** (permiso o estado), no se pasa: se omite;
 * - si aplica pero está **bloqueada ahora** (por ejemplo, borrar un modelo con unidades), se pasa con
 *   `disabled` y `disabledReason`, y ese motivo se lee en el `title` y en el lector de pantalla.
 *   El `title` va en un envoltorio porque un botón `disabled` no recibe el ratón.
 */
export interface RowAction {
  /** Identificador estable, para las pruebas y las claves de React. */
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** `true` para acciones que destruyen datos: se pintan en rojo. */
  destructive?: boolean;
  disabled?: boolean;
  /** Motivo por el que no se puede pulsar; sustituye a la etiqueta cuando está deshabilitada. */
  disabledReason?: string;
}

export function RowActions({ actions, className }: { actions: RowAction[]; className?: string }) {
  if (actions.length === 0) return null;
  return (
    <div
      className={`flex items-center justify-end gap-1 ${className ?? ''}`}
      // Los clics de los iconos no deben abrir la ficha de la fila.
      onClick={(event) => event.stopPropagation()}
    >
      {actions.map((action) => (
        action.disabled ? (
          // Un botón `disabled` no recibe eventos del ratón, así que su `title` no se ve nunca: el
          // motivo va en el envoltorio, y el texto oculto lo lee el lector de pantalla.
          <span key={action.key} title={action.disabledReason ?? action.label} className="inline-flex">
            <Button type="button" variant="ghost" size="icon-sm" aria-label={action.label} disabled>
              {action.icon}
            </Button>
            <span className="sr-only">{action.disabledReason ?? action.label}</span>
          </span>
        ) : (
          <Button
            key={action.key}
            type="button"
            variant="ghost"
            size="icon-sm"
            title={action.label}
            aria-label={action.label}
            className={action.destructive ? 'text-destructive hover:text-destructive' : undefined}
            onClick={action.onClick}
          >
            {action.icon}
          </Button>
        )
      ))}
    </div>
  );
}
